import {
  ContentStepWritable,
  PlanUpdateRequestWritable,
} from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

const isDocumentStepMissingReference = (step: ContentStepWritable): boolean =>
  step.type === Constants.STEP_TYPES.DOCUMENT && !step.reference;

// This module exists to report payloads that violate the generated contract,
// so it must tolerate steps whose `name` is absent even though the type
// declares it required.
const stepName = (step: {
  name?: Record<string, string>;
}): string | undefined => step.name?.en ?? step.name?.es;

/**
 * Returns field-path strings for document steps that are missing a `reference`
 * value (null, undefined, or empty string) in a plan PUT payload. Used to
 * surface data-loss regressions — a document step without a reference produces
 * a broken link — before they reach the API silently.
 */
export const getMissingFields = (
  payload: PlanUpdateRequestWritable,
): Record<string, string | undefined>[] => {
  const missing: Record<string, string | undefined>[] = [];

  const { steps, sections } = payload.content ?? {};

  steps?.forEach((step, i) => {
    if (isDocumentStepMissingReference(step)) {
      missing.push({
        missingField: `content.steps[${i}].reference`,
        name: stepName(step),
        stepId: step.id,
      });
    }
  });

  sections?.forEach((section, sIdx) => {
    section.steps?.forEach((step, stIdx) => {
      if (isDocumentStepMissingReference(step)) {
        missing.push({
          missingField: `content.sections[${sIdx}].steps[${stIdx}].reference`,
          name: stepName(step),
          stepId: step.id,
          sectionId: section.id,
        });
      }
    });
  });

  return missing;
};
